import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  downloadCardAsImage,
  copyToClipboard,
  generateCardAsBlob,
  shareImageWithPlatform,
  generateShareText,
  generateCaption,
  generateReactionCount,
  isFileShareSupported,
} from "../utils/shareUtils";
import "./ResultCard.css";

export const ResultCard = ({ horoscope, onTryAnother, loading, error }) => {
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [shareLoading, setShareLoading] = useState(null);
  const reactionCount = generateReactionCount();

  if (error) {
    return (
      <motion.section
        className="result-section error-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="error-message">
          <p className="error-text">{error}</p>
          <button className="retry-btn" onClick={onTryAnother}>
            Try Again 🔄
          </button>
        </div>
      </motion.section>
    );
  }

  if (!horoscope) return null;

  const handleCopyText = async () => {
    const shareText = generateShareText(
      horoscope.name,
      horoscope.zodiacSign.name,
      horoscope.bulletPoints.join("\n")
    );
    const success = await copyToClipboard(shareText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyCaption = async () => {
    const caption = generateCaption(horoscope.name, horoscope.zodiacSign.name);
    const success = await copyToClipboard(caption);
    if (success) {
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    }
  };

  const handleDownloadCard = async () => {
    try {
      setDownloadError(null);
      setShareLoading("download");
      await downloadCardAsImage("horoscope-card", `${horoscope.name}-horoscope.png`, 2);
      setShareLoading(null);
    } catch (err) {
      setDownloadError(err.message);
      setShareLoading(null);
    }
  };

  const handleShareToInstagram = async () => {
    try {
      setShareLoading("instagram");
      // Download image silently
      await downloadCardAsImage("horoscope-card", `${horoscope.name}-horoscope.png`, 2);
      setShareLoading(null);
      
      // Show instruction modal
      const message = "📸 Image saved to your gallery!\n\nNow:\n1. Open Instagram\n2. Tap + or Your Story\n3. Select this image\n4. Post to Story! 🔥";
      alert(message);
      
      // Try to open Instagram app
      try {
        window.open('instagram://story-camera', '_blank');
      } catch (e) {
        window.open('https://instagram.com', '_blank');
      }
    } catch (err) {
      console.error("Error sharing to Instagram:", err);
      setShareLoading(null);
    }
  };

  const handleShareToTikTok = async () => {
    try {
      setShareLoading("tiktok");
      // Download image silently
      await downloadCardAsImage("horoscope-card", `${horoscope.name}-horoscope.png`, 2);
      setShareLoading(null);
      
      // Show instruction modal
      const message = "📸 Image saved!\n\nNow:\n1. Open TikTok\n2. Tap + at bottom\n3. Select image from gallery\n4. Post as Story or Video! 🎵";
      alert(message);
      
      // Try to open TikTok app
      try {
        window.open('tiktok://app', '_blank');
      } catch (e) {
        window.open('https://tiktok.com', '_blank');
      }
    } catch (err) {
      console.error("Error sharing to TikTok:", err);
      setShareLoading(null);
    }
  };

  const handleShareToWhatsApp = async () => {
    try {
      setShareLoading("whatsapp");
      const blob = await generateCardAsBlob("horoscope-card", 2);
      await shareImageWithPlatform(blob, "WhatsApp");
      setShareLoading(null);
    } catch (err) {
      console.error("Error sharing to WhatsApp:", err);
      setShareLoading(null);
      if (!isFileShareSupported()) {
        handleDownloadCard();
        alert("Image downloaded! Open WhatsApp and share it manually. 💬");
      }
    }
  };
  
  const handleChallengeWhatsApp = () => {
    const message = "I just got roasted by my horoscope 💀\nTry yours at wtfhoroscope.xyz 👀\n#WTFHoroscope";
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleRegenerateHoroscope = async () => {
    // This would be called with the same parameters but fresh API call
    // We'll trigger this through parent component
    onTryAnother();
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      className="result-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Main Card */}
      <motion.div
        id="horoscope-card"
        className="horoscope-card"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Animated Stars Background */}
        <div className="card-stars">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="star" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
            }} />
          ))}
        </div>

        {/* Header with Logo and Zodiac */}
        <div className="card-header">
          <div className="card-logo">WTFHoroscope.xyz</div>
          <div className="card-zodiac-emoji">{horoscope.zodiacSign.emoji}</div>
        </div>

        {/* Greeting */}
        <div className="card-greeting">
          Hey {horoscope.name} 👀
        </div>

        {/* Zodiac Sign */}
        <div className="card-zodiac-name">
          {horoscope.zodiacSign.name}
        </div>

        {/* 5 Bullet Points */}
        <div className="card-bullets">
          {horoscope.bulletPoints.map((bullet, index) => (
            <div
              key={index}
              className={`bullet-point bullet-${index + 1} ${
                index < 2 ? "funny" : "real"
              }`}
            >
              <span className="bullet-dot">•</span>
              <span className="bullet-text">{bullet}</span>
            </div>
          ))}
        </div>

        {/* Cosmic Verdict */}
        <div className="cosmic-verdict">
          <em>{horoscope.cosmicVerdict}</em>
        </div>

        {/* Lucky Row */}
        <div className="lucky-row">
          <div className="lucky-pill">
            <span className="lucky-label">Lucky #</span>
            <span className="lucky-value">{horoscope.luckyNumber}</span>
          </div>
          <div className="lucky-pill">
            <span className="lucky-label">Color</span>
            <span className="color-swatch" style={{ backgroundColor: horoscope.luckyColor }}></span>
          </div>
          <div className="lucky-pill">
            <span className="lucky-label">Vibe</span>
            <span className="lucky-value">{horoscope.vibe}</span>
          </div>
        </div>

        {/* Watermark */}
        <div className="card-watermark">
          WTFHoroscope.xyz
        </div>
      </motion.div>

      {/* Share Buttons - Mobile Grid (2x2 + Save) */}
      <motion.div
        className="share-buttons-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        {/* Save Button - Full Width */}
        <button
          className="share-btn save-btn"
          onClick={handleDownloadCard}
          disabled={shareLoading === "download"}
          title="Save card as image"
        >
          <span className="btn-icon">📸</span>
          <span className="btn-label">
            {shareLoading === "download" ? "Saving..." : "Save Card 📸"}
          </span>
        </button>

        {/* Share Grid */}
        <div className="share-grid">
          {/* Instagram */}
          <button
            className="share-btn instagram-btn"
            onClick={handleShareToInstagram}
            disabled={shareLoading === "instagram"}
            title="Share to Instagram Story"
          >
            <span className="btn-icon">📱</span>
            <span className="btn-label">
              {shareLoading === "instagram" ? "..." : "Instagram"}
            </span>
            <span className="btn-hint">Story</span>
          </button>

          {/* TikTok */}
          <button
            className="share-btn tiktok-btn"
            onClick={handleShareToTikTok}
            disabled={shareLoading === "tiktok"}
            title="Share to TikTok"
          >
            <span className="btn-icon">🎵</span>
            <span className="btn-label">
              {shareLoading === "tiktok" ? "..." : "TikTok"}
            </span>
            <span className="btn-hint">Story</span>
          </button>

          {/* WhatsApp */}
          <button
            className="share-btn whatsapp-btn"
            onClick={handleShareToWhatsApp}
            disabled={shareLoading === "whatsapp"}
            title="Send on WhatsApp"
          >
            <span className="btn-icon">💬</span>
            <span className="btn-label">
              {shareLoading === "whatsapp" ? "..." : "WhatsApp"}
            </span>
            <span className="btn-hint">Send</span>
          </button>

          {/* Copy for Twitter */}
          <button
            className="share-btn twitter-btn"
            onClick={handleCopyText}
            title="Copy for Twitter/X"
          >
            <span className="btn-icon">🐦</span>
            <span className="btn-label">
              {copied ? "Copied! ✓" : "Twitter"}
            </span>
            <span className="btn-hint">Copy</span>
          </button>
        </div>
      </motion.div>

      {/* Caption Section */}
      <motion.div
        className="caption-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="caption-box">
          <p className="caption-text">
            {horoscope.name}'s {horoscope.zodiacSign.name} horoscope just called them out 💀<br />
            Try yours at WTFHoroscope.xyz<br />
            #WTFHoroscope #{horoscope.zodiacSign.name}Season #Horoscope #Astrology
          </p>
          <button
            className="copy-caption-btn"
            onClick={handleCopyCaption}
            title="Copy caption"
          >
            {captionCopied ? "Copied! ✓" : "Copy Caption"}
          </button>
        </div>
      </motion.div>

      {/* Challenge Section */}
      <motion.div
        className="challenge-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <p className="challenge-text">
          Think your friends can handle their WTF reading? 👀
        </p>
        <button className="challenge-btn" onClick={handleChallengeWhatsApp} title="Challenge a friend">
          Challenge Your Friends 😈
        </button>
      </motion.div>

      {/* Reaction Counter */}
      <motion.div
        className="reaction-counter"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        🔥 {reactionCount.toLocaleString()} people got roasted today
      </motion.div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <motion.button
          className="regenerate-btn"
          onClick={handleRegenerateHoroscope}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Generate new prediction"
        >
          Regenerate 🔄
        </motion.button>

        <motion.button
          className="try-another-btn"
          onClick={onTryAnother}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Try another sign"
        >
          Try Another Sign ✨
        </motion.button>
      </div>

      {downloadError && (
        <p className="error-text" style={{ marginTop: "1rem", textAlign: "center" }}>
          {downloadError}
        </p>
      )}
    </motion.section>
  );
};
